import { apiError } from '../apiError';
import axiosInstance from '../axios.config';
import { ApiResponse } from 'types';

/**
 * Email campaigns — provider config, templates, and campaigns.
 * Backend: core-service /api/v1/email/*  (mirrors the WhatsApp campaign API).
 */

export type EmailCampaignStatus =
  // STANDING is where a standing campaign RESTS when idle — deliberately not RUNNING,
  // because the orphan reclaimer requeues a RUNNING campaign that has not heartbeat in
  // five minutes and an idle bucket never heartbeats.
  | 'DRAFT' | 'SCHEDULED' | 'RUNNING' | 'STANDING' | 'PAUSED' | 'COMPLETED' | 'FAILED' | 'CANCELLED';

export interface EmailProvider {
  id?: string;
  name?: string;                     // label distinguishing an org's several senders
  isDefault?: boolean;               // the sender used when a campaign names none
  providerType?: string;            // 'SES' | 'SMTP'
  sesRegion?: string;
  usePlatformAccount?: boolean;      // true = send from OUR account; false = org's own keys
  sesAccessKey?: string;
  sesSecretKeySet?: boolean;         // read-only signal that a secret is stored
  sesSecretKey?: string;             // write-only; never returned
  smtpHost?: string;
  smtpPort?: number | null;
  smtpUsername?: string;
  smtpPassword?: string;             // write-only; never returned
  smtpPasswordSet?: boolean;         // read-only signal that a password is stored
  smtpUseTls?: boolean;
  fromEmail?: string;
  fromName?: string;
  replyToEmail?: string;
  maxSendRatePerSecond?: number | null;
  dailyQuota?: number | null;
  active?: boolean;
  lastVerifiedAt?: string | null;
  lastError?: string | null;
}

/**
 * One static attachment on a template.
 *
 * Produced by the upload endpoint and stored verbatim — the client appends what
 * the server returned rather than assembling this shape itself, so a renamed
 * field cannot silently become a template that attaches nothing.
 */
export interface EmailAttachment {
  url: string;
  filename: string;
  contentType?: string;
  sizeBytes?: number;
}

export interface EmailTemplate {
  id?: string;
  name: string;
  description?: string;
  category?: string;
  subject: string;
  htmlBody?: string;
  textBody?: string;
  variables?: string[];
  /** Static files sent with every message from this template. */
  attachments?: EmailAttachment[];
  active?: boolean;
  createdAt?: string;
  updatedAt?: string;
}

export interface EmailRecipientInput {
  email: string;
  contactId?: string;
  variables?: Record<string, string>;
}

/**
 * A campaign's report.
 *
 * NOT the WhatsApp funnel with the labels changed. WhatsApp reports
 * sent -> delivered -> read because Meta tells us each of those; email has no
 * read receipt, so this is sent -> opened -> clicked, and "opened" is an image
 * fetch rather than a person — see `engagement.caveat`.
 */
export interface EmailCampaignInsights {
  funnel: { total: number; sent: number; opened: number; clicked: number; failed: number; skipped: number };
  engagement: {
    uniqueOpens: number;
    uniqueOpensExcludingMachineFetches: number;
    totalOpens: number;
    uniqueClicks: number;
    totalClicks: number;
    openRate: number | null;
    clickRate: number | null;
    clickToOpenRate: number | null;
    caveat: string;
  };
  errorAnalysis: { reason: string; count: number }[];
  skipAnalysis: { reason: string; count: number }[];
  links: { url: string; clicks: number; uniqueClicks: number }[];
}

export interface EmailSuppression {
  id: string;
  email: string;
  reason: 'UNSUBSCRIBED' | 'BOUNCED' | 'COMPLAINED' | 'MANUAL';
  sourceCampaignId?: string | null;
  notes?: string | null;
  createdAt?: number;
}

export interface EmailCampaign {
  id?: string;
  name: string;
  description?: string;
  templateId?: string;
  templateName?: string;
  emailProviderId?: string;
  fromEmail?: string;
  fromName?: string;
  status?: EmailCampaignStatus;
  /** A bucket that never completes — fed one recipient at a time, rests in STANDING. */
  standing?: boolean;
  totalRecipients?: number;
  /**
   * Accepted by the provider. NOT the same as delivered: a bounce arrives later and
   * is counted separately rather than decremented from here, so this stays the answer
   * to "how many did the provider take" — the number you want when diagnosing a
   * throttle or a bad credential.
   */
  emailsSent?: number;
  acceptedByProvider?: number;
  deliveredNotBounced?: number;
  bouncedOrFailed?: number;
  recipientStatus?: Record<string, number>;
  emailsFailed?: number;
  emailsSkipped?: number;
  sendRate?: number;
  suppressRecentDays?: number | null;
  scheduledAt?: string | null;
  completedAt?: string | null;
  createdAt?: string;
}

const P = '/v1/email/provider';
const T = '/v1/email/templates';
const C = '/v1/email/campaigns';
const err = (e: any, msg: string) => apiError(e, msg);

export const emailService = {
  // ---- providers (collection; an org may hold several senders) ----
  listProviders: async (): Promise<ApiResponse<EmailProvider[]>> => {
    try { return (await axiosInstance.get(P)).data; }
    catch (e: any) { return err(e, 'Failed to load email senders'); }
  },
  createProvider: async (data: EmailProvider): Promise<ApiResponse<EmailProvider>> => {
    try { return (await axiosInstance.post(P, data)).data; }
    catch (e: any) { return err(e, 'Failed to create sender'); }
  },
  updateProvider: async (id: string, data: EmailProvider): Promise<ApiResponse<EmailProvider>> => {
    try { return (await axiosInstance.put(`${P}/${id}`, data)).data; }
    catch (e: any) { return err(e, 'Failed to save sender'); }
  },
  deleteProvider: async (id: string): Promise<ApiResponse<string>> => {
    try { return (await axiosInstance.delete(`${P}/${id}`)).data; }
    catch (e: any) { return err(e, 'Failed to delete sender'); }
  },
  setDefaultProvider: async (id: string): Promise<ApiResponse<EmailProvider>> => {
    try { return (await axiosInstance.post(`${P}/${id}/default`)).data; }
    catch (e: any) { return err(e, 'Failed to set default sender'); }
  },
  testProvider: async (id: string, to: string): Promise<ApiResponse<{ messageId: string }>> => {
    try { return (await axiosInstance.post(`${P}/${id}/test`, { to })).data; }
    catch (e: any) { return err(e, 'Test send failed'); }
  },

  // ---- templates ----
  listTemplates: async (): Promise<ApiResponse<EmailTemplate[]>> => {
    try { return (await axiosInstance.get(T)).data; }
    catch (e: any) { return err(e, 'Failed to load templates'); }
  },
  getTemplate: async (id: string): Promise<ApiResponse<EmailTemplate>> => {
    try { return (await axiosInstance.get(`${T}/${id}`)).data; }
    catch (e: any) { return err(e, 'Failed to load template'); }
  },
  /**
   * Upload one file and get back the entry to append to template.attachments.
   *
   * Uploading does NOT attach it — the template still has to be saved. That split
   * is deliberate: a half-finished edit must not start mailing a document to
   * everyone the next time a campaign runs.
   */
  uploadTemplateAttachment: async (file: File): Promise<ApiResponse<EmailAttachment>> => {
    try {
      const form = new FormData();
      form.append('file', file);
      return (await axiosInstance.post(`${T}/attachments`, form)).data;
    } catch (e: any) { return err(e, 'Failed to upload attachment'); }
  },
  createTemplate: async (data: EmailTemplate): Promise<ApiResponse<EmailTemplate>> => {
    try { return (await axiosInstance.post(T, data)).data; }
    catch (e: any) { return err(e, 'Failed to create template'); }
  },
  updateTemplate: async (id: string, data: EmailTemplate): Promise<ApiResponse<EmailTemplate>> => {
    try { return (await axiosInstance.put(`${T}/${id}`, data)).data; }
    catch (e: any) { return err(e, 'Failed to update template'); }
  },
  deleteTemplate: async (id: string): Promise<ApiResponse<string>> => {
    try { return (await axiosInstance.delete(`${T}/${id}`)).data; }
    catch (e: any) { return err(e, 'Failed to delete template'); }
  },
  previewTemplate: async (id: string, sample: Record<string, string>):
    Promise<ApiResponse<{ subject: string; htmlBody: string; textBody: string; unresolved: string[] }>> => {
    try { return (await axiosInstance.post(`${T}/${id}/preview`, { sample })).data; }
    catch (e: any) { return err(e, 'Failed to preview template'); }
  },

  // ---- campaigns ----
  listCampaigns: async (): Promise<ApiResponse<EmailCampaign[]>> => {
    try { return (await axiosInstance.get(C)).data; }
    catch (e: any) { return err(e, 'Failed to load campaigns'); }
  },
  getCampaign: async (id: string): Promise<ApiResponse<EmailCampaign>> => {
    try { return (await axiosInstance.get(`${C}/${id}`)).data; }
    catch (e: any) { return err(e, 'Failed to load campaign'); }
  },
  getRecipients: async (id: string): Promise<ApiResponse<any[]>> => {
    try { return (await axiosInstance.get(`${C}/${id}/recipients`)).data; }
    catch (e: any) { return err(e, 'Failed to load recipients'); }
  },
  createCampaign: async (data: {
    name: string; description?: string; templateId: string; providerId?: string;
    sendRate?: number; suppressRecentDays?: number | null;
    /** A campaign that stays open and is fed one recipient at a time. */
    standing?: boolean;
    recipients: EmailRecipientInput[];
  }): Promise<ApiResponse<EmailCampaign>> => {
    try { return (await axiosInstance.post(C, data)).data; }
    catch (e: any) { return err(e, 'Failed to create campaign'); }
  },
  /** Paged, filterable recipients — what the detail screen uses. */
  getRecipientsPage: async (
    id: string,
    opts: { status?: string; search?: string; page?: number; size?: number } = {},
  ): Promise<ApiResponse<{ content: any[]; page: number; size: number; totalElements: number; totalPages: number }>> => {
    try {
      const params = new URLSearchParams();
      if (opts.status && opts.status !== 'ALL') params.set('status', opts.status);
      if (opts.search) params.set('search', opts.search);
      params.set('page', String(opts.page ?? 0));
      params.set('size', String(opts.size ?? 50));
      return (await axiosInstance.get(`${C}/${id}/recipients/page?${params}`)).data;
    } catch (e: any) { return err(e, 'Failed to load recipients'); }
  },

  /**
   * Read an uploaded CSV or XLSX WITHOUT creating anything.
   *
   * Returns header-keyed ROWS, not recipients — which column holds the address, and
   * which fills which template variable, stays the user's decision. A parser that
   * guessed would be wrong exactly when a sheet is unusual, and silently.
   *
   * The server does the reading because a browser cannot open XLSX and because CSV
   * quoting is easy to get subtly wrong; nothing is stored by this call.
   */
  parseRecipients: async (file: File): Promise<ApiResponse<{
    headers: string[]; rows: Record<string, string>[]; totalRows: number; truncated: boolean;
  }>> => {
    try {
      const form = new FormData();
      form.append('file', file);
      return (await axiosInstance.post(`${C}/recipients/parse`, form)).data;
    } catch (e: any) { return err(e, 'Failed to read that file'); }
  },

  /**
   * Send one real email to yourself before launching to everybody.
   *
   * Nothing is created and no counter moves — no campaign, no recipient row, no
   * tracking. Without this the only way to see what a campaign looks like was to
   * launch a throwaway campaign of one, which leaves a fake campaign in the list
   * and a fake row in every total.
   *
   * The subject is deliberately NOT prefixed with anything: the point is to see
   * exactly what a recipient gets.
   */
  testSend: async (body: { to: string; templateId: string; variables?: Record<string, string> }):
    Promise<ApiResponse<Record<string, any>>> => {
    try { return (await axiosInstance.post(`${C}/test-send`, body)).data; }
    catch (e: any) { return err(e, 'Test send failed'); }
  },

  /** Drop one person into a standing campaign. Returns PENDING — the scheduler sends it. */
  enrol: async (id: string, body: { email: string; variables?: Record<string, string>; contactId?: string }):
    Promise<ApiResponse<{ recipientId: string; campaignId: string; status: string; note?: string }>> => {
    try { return (await axiosInstance.post(`${C}/${id}/enrol`, body)).data; }
    catch (e: any) { return err(e, 'Failed to enrol'); }
  },

  /**
   * Exactly what one recipient received — the stored copy, never re-rendered.
   *
   * Re-rendering would answer "what would we send now", which is a different
   * question and wrong the moment the template is edited. The reason to open this
   * is usually that somebody is disputing what they got.
   */
  sentCopy: async (id: string, recipientId: string): Promise<ApiResponse<{
    recipientId: string; email: string; status: string; errorCode?: string | null;
    sentAt?: string | null; providerMessageId?: string | null;
    stored: boolean; subject?: string | null; html?: string | null; note?: string;
  }>> => {
    try { return (await axiosInstance.get(`${C}/${id}/recipients/${recipientId}/sent`)).data; }
    catch (e: any) { return err(e, 'Failed to load the sent copy'); }
  },

  getInsights: async (id: string): Promise<ApiResponse<EmailCampaignInsights>> => {
    try { return (await axiosInstance.get(`${C}/${id}/insights`)).data; }
    catch (e: any) { return err(e, 'Failed to load insights'); }
  },

  getStats: async (): Promise<ApiResponse<any>> => {
    try { return (await axiosInstance.get(`${C}/stats`)).data; }
    catch (e: any) { return err(e, 'Failed to load stats'); }
  },

  cancelCampaign: async (id: string): Promise<ApiResponse<EmailCampaign>> => {
    try { return (await axiosInstance.post(`${C}/${id}/cancel`)).data; }
    catch (e: any) { return err(e, 'Failed to cancel campaign'); }
  },

  resendFailed: async (id: string): Promise<ApiResponse<EmailCampaign>> => {
    try { return (await axiosInstance.post(`${C}/${id}/resend-failed`)).data; }
    catch (e: any) { return err(e, 'Failed to create the resend campaign'); }
  },

  // ── the suppression list ──────────────────────────────────────────────────
  listSuppressions: async (
    opts: { reason?: string; page?: number; size?: number } = {},
  ): Promise<ApiResponse<{ content: EmailSuppression[]; totalElements: number; totalPages: number; page: number }>> => {
    try {
      const params = new URLSearchParams();
      if (opts.reason) params.set('reason', opts.reason);
      params.set('page', String(opts.page ?? 0));
      params.set('size', String(opts.size ?? 50));
      return (await axiosInstance.get(`/v1/email/suppressions?${params}`)).data;
    } catch (e: any) { return err(e, 'Failed to load the suppression list'); }
  },

  suppressionCounts: async (): Promise<ApiResponse<Record<string, number>>> => {
    try { return (await axiosInstance.get('/v1/email/suppressions/counts')).data; }
    catch (e: any) { return err(e, 'Failed to load suppression counts'); }
  },

  addSuppression: async (email: string, reason = 'MANUAL', notes?: string): Promise<ApiResponse<any>> => {
    try { return (await axiosInstance.post('/v1/email/suppressions', { email, reason, notes })).data; }
    catch (e: any) { return err(e, 'Failed to add the address'); }
  },

  importSuppressions: async (emails: string[], reason = 'MANUAL', notes?: string): Promise<ApiResponse<any>> => {
    try { return (await axiosInstance.post('/v1/email/suppressions/import', { emails, reason, notes })).data; }
    catch (e: any) { return err(e, 'Failed to import the list'); }
  },

  removeSuppression: async (email: string): Promise<ApiResponse<any>> => {
    try { return (await axiosInstance.delete(`/v1/email/suppressions?email=${encodeURIComponent(email)}`)).data; }
    catch (e: any) { return err(e, 'Failed to remove the address'); }
  },

  startCampaign: async (id: string): Promise<ApiResponse<EmailCampaign>> => {
    try { return (await axiosInstance.post(`${C}/${id}/start`)).data; }
    catch (e: any) { return err(e, 'Failed to start campaign'); }
  },
  pauseCampaign: async (id: string): Promise<ApiResponse<EmailCampaign>> => {
    try { return (await axiosInstance.post(`${C}/${id}/pause`)).data; }
    catch (e: any) { return err(e, 'Failed to pause campaign'); }
  },
};
